import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { defaultContainerSize, mq } from 'styles/mixins';
import {
  Header2,
  ParagraphM,
  ParagraphS,
} from '../../core/Typography/Typography';
import { THEME } from '../../../common/consts/theme';

export const LinkMore = styled.a`
  text-decoration: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.875rem;
  border-radius: 3.75rem;
  margin-top: 2rem;
  width: fit-content;
  background: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

export const LinkText = styled.h2`
  ${ParagraphM};
  font-family: ${({ theme }) => theme.font.questrial};
  margin: 0;
  font-weight: 400;
  letter-spacing: 0.0125rem;
  text-align: left;
  color: ${({ theme }) => theme.colors.core_white};
`;

export const ServiceScrollAnchor = styled.div`
  transform: translateY(-100px);
`;

export const ServiceTitle = styled.p`
  ${Header2};
  position: relative;
  flex-direction: row;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.core_white};
  letter-spacing: -0.013rem;
  margin: 0 0 1.5rem 0;

  ${mq.small(css`
    margin: 0 0 0.5rem 0;
  `)};

  ${mq.xLarge(css`
    font-size: 2.5rem;
    line-height: 3.5rem;
    padding-bottom: 2rem;
  `)};
`;

export const ServiceParagraph = styled.p`
  ${ParagraphS};
  letter-spacing: 0.013rem;
  color: ${({ theme }) => theme.colors.light_grey};
  padding: 0;
  margin: 0;
`;

export const ServiceListItem = styled.li`
  ${ParagraphS};
  font-size: 1.125rem;
  line-height: 1.813rem;
  position: relative;
  border: solid 0.0625rem transparent;

  &:not(:first-child) {
    margin-top: 1rem;
  }

  &:before {
    content: '';
    position: absolute;
    height: 0.0625rem;
    right: 0;
    bottom: 0;
    left: 0;
    margin: -0.3125rem;
    border-radius: inherit;
    background: linear-gradient(89.99deg, #f94743 -15.08%, #0e4ed7 105.67%);
  }
}
`;

export const ServiceList = styled.ul`
  align-self: flex-end;
  width: 100%;
  padding: 0 0 0 1rem;
  box-sizing: border-box;
  margin: 0;

  ${mq.xLarge(css`
    width: 23.125rem;
  `)};
`;

export const ServiceContent = styled.div`
  ${mq.medium(css`
    max-width: 21.625rem;
  `)};

  ${mq.large(css`
    max-width: 23.063rem;
  `)};

  h3 {
    ${ParagraphM};
    font-weight: 700;
    font-size: 1.125rem;
    margin: 1.5rem 0 0.5rem 0;

    ${mq.xLarge(css`
      margin: 2rem 0 0.5rem 0;
    `)};
  }
`;

export const ServicesSection = styled.section`
  background-color: ${({ theme }) => theme.colors.core_black};
`;

export const ServicesSectionInner = styled.div`
  ${defaultContainerSize};
  padding: 0;
  display: flex;
  flex-direction: column;

  ${mq.medium(css`
    padding: 0;
  `)};

  ${mq.xLarge(css`
    flex-direction: row;
    padding: 0 7.5rem;
  `)};
`;

export const Navigation = styled.ul`
  position: sticky;
  height: auto;
  list-style: none;
  margin: 0;
  padding: 0;
  top: 3.75rem;
  display: flex;
  width: 100%;
  overflow-x: auto;
  background-color: ${({ theme }) => theme.colors.core_black};
  z-index: 50;
  box-shadow: inset 0em -2px ${({ theme }) => theme.colors.darker_grey};

  ${mq.large(css`
    justify-content: center;
  `)};

  ${mq.xLarge(css`
    margin: 11rem 0 25rem 0;
    flex-direction: column;
    height: 16rem;
    overflow: hidden;
    width: 18.75rem;
    top: 12rem;
    box-shadow: none;
  `)};
`;

type NavigationItemType = {
  isActive: boolean;
};

export const NavigationItem = styled.li<NavigationItemType>`
  ${ParagraphM};
  color: ${({ theme }) => theme.colors.grey};
  font-family: ${({ theme }) => theme.font.questrial};
  font-style: normal;
  font-weight: 400;
  opacity: 0.5;
  white-space: nowrap;
  padding: 1rem 1.5rem 1.25rem 1.5rem;
  position: relative;
  cursor: pointer;

  ${mq.medium(css`
    padding: 1rem 2rem 1.25rem 2rem;
  `)};

  ${mq.xLarge(css`
    padding: 0;
    margin-bottom: 2rem;
    border: none;
  `)};

  ${({ isActive }) =>
    isActive &&
    css`
      animation: ${navItemColor} 500ms;
      color: ${THEME.colors.core_white};
      opacity: 1;

      &:after {
        content: '';
        position: absolute;
        height: 2px;
        right: 0;
        bottom: 0;
        left: 0;
        border-radius: inherit;
        background: linear-gradient(89.99deg, #0e4ed7 -15.08%, #f94743 93.47%);
        z-index: 51;

        ${mq.xLarge(css`
          display: none;
        `)};
      }
    `}
`;

const navItemColor = keyframes`
  from {
    color: ${THEME.colors.grey};
    opacity: 0.5;
  }
  to {
    transform:  ${THEME.colors.core_white};
    opacity: 1;
  }
`;

export const ServicesSlides = styled.div`
  display: flex;
  flex-direction: column;
  width: auto;

  ${mq.xLarge(css`
    width: 75%;
  `)};
`;
